import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { BookOpen } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const CCAI_DOMAIN = '@chinesecommunity.ai';

export function isCcaiEmail(email) {
  return typeof email === 'string' && email.toLowerCase().endsWith(CCAI_DOMAIN);
}

function GateScreen({ title, message, action }) {
  return (
    <div className="flex h-screen flex-col items-center justify-center bg-white px-6 text-center">
      <BookOpen className="h-10 w-10 text-blue-600 mb-4" />
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">{title}</h1>
      <p className="max-w-md text-sm text-gray-500 mb-6">{message}</p>
      {action}
    </div>
  );
}

const WikiAccessGate = ({ children }) => {
  const { isAuthenticated, loading, isDevMode, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center bg-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  // Localhost dev mode bypasses the gate so the wiki is editable locally.
  if (isDevMode) return children;

  if (!isAuthenticated) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return (
      <GateScreen
        title="Log in with your CCAI email to learn"
        message="The Chinese Community AI wiki is for team members. Sign in with your @chinesecommunity.ai account to read the bootcamps and contribute."
        action={
          <Link
            to={`/login?redirect=${redirect}`}
            className="rounded-full bg-gray-900 px-6 py-2.5 text-sm font-semibold text-white hover:bg-gray-700"
          >
            Log in
          </Link>
        }
      />
    );
  }

  if (!isCcaiEmail(user?.email)) {
    return (
      <GateScreen
        title="CCAI email required"
        message={`Wiki access is limited to @chinesecommunity.ai accounts. You're signed in as ${user?.email || 'an external account'}.`}
        action={
          <Link to="/" className="text-sm font-medium text-blue-600 hover:underline">
            Return to site
          </Link>
        }
      />
    );
  }

  return children;
};

export default WikiAccessGate;
